import {
  Components,
  ComputeItemKey,
  ItemContent,
  SizeFunction,
  Virtuoso,
} from 'react-virtuoso';
import { Box, Typography } from '@mui/material';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { API } from 'api';
import { AxiosResponse } from 'axios';
import { InlineStatus } from 'components/InlineStatus';
import { MAIN_REFETCH_INTERVAL_MS } from 'features/mapping/mapping.constants';
import { useCacheState } from 'state/useCacheState';
import { makeStyles } from 'tss-react/mui';
import { Report, ReportMedia } from 'features/reports/report.types';
import { useTranslation } from 'react-i18next';
import {
  MouseEvent,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { Capacitor } from '@capacitor/core';
import { getDeviceInfo } from 'state/localStorageTyped';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Flooding } from 'features/flooding/flooding.types';
import { isSafari } from 'shared/utils';
import { ReportableGeoEventType } from 'features/geoEvents/geoEvent.types';
import { isEditReportDisabled, usePermissions } from 'hooks/usePermissions';
import { useEditReportActionMenuState } from 'state/useEditReportActionMenuState';
import { ReportCard } from './ReportCard';

type ReportPaginatedData = {
  count: number;
  results: Report[];
  next: string | null;
  previous: string | null;
};

type GeoEventReportsProps = {
  geoEventId: number;
  geoEventType: ReportableGeoEventType;
};

type ReportsListContext = {
  isFetchingNextPage: boolean;
  bottomReached: boolean;
};

// Virtuoso will anchor the scroll position to this index. Polling prepends newly published
// reports, so we decrease it by the number prepended to keep the reader where they were
// rather than pushing them down the list. Any very big number works for this purpose.
const VIRTUOSO_START_INDEX = 100_000;

const fetchReports = (
  geoEventId: number,
  { limit, offset }: { limit: number; offset: number },
  cacheBusterTs: number | null,
): Promise<AxiosResponse<ReportPaginatedData>> => {
  let url = `reports/?geo_event_id=${geoEventId}&status=approved&limit=${limit}&offset=${offset}`;
  if (cacheBusterTs) {
    url += `&ts=${cacheBusterTs}`;
  }
  return API.get<ReportPaginatedData>(url);
};

const useStyles = makeStyles()((theme) => ({
  loadingSpinner: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const PAGINATION_LIMIT = 20;

const extractOffset = (url: string): number | null => {
  const offsetMatch = url.match(/[?&]offset=(\d+)/);
  if (!offsetMatch) {
    return null;
  }
  return parseInt(offsetMatch[1], 10);
};

const LoadingSpinner = (): JSX.Element => {
  const { classes } = useStyles();
  return (
    <Box
      className={classes.loadingSpinner}
      data-testid="reports-list-loading-spinner"
    >
      <InlineStatus status="loading" />
    </Box>
  );
};

const getReportMedia = (media: ReportMedia[]): ReportMedia | null =>
  media.length ? media[0] : null;

const ListFooter = (props: {
  loading: boolean;
  bottomReached: boolean;
}): JSX.Element | null => {
  const { loading, bottomReached } = props;
  const { t } = useTranslation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (bottomReached) {
    return (
      <Typography
        gutterBottom
        variant="body2"
        component="p"
        color="textSecondary"
        align="center"
        data-testid="reports-list-end"
        sx={{ pt: 2 }}
      >
        {t('geoEvent.reports.bottomReached')}
      </Typography>
    );
  }

  return null;
};

// Defined outside the component so the identities never change. Virtuoso hands these to React as
// element types, so rebuilding them during render would unmount and remount every rendered item (which is bad)
const virtuosoComponents: Components<Report, ReportsListContext> = {
  Item: ({ children, item, context, ...itemProps }) => (
    <div {...itemProps} data-id={item.id}>
      {children}
    </div>
  ),
  Footer: ({ context }) => (
    <ListFooter
      loading={context.isFetchingNextPage}
      bottomReached={context.bottomReached}
    />
  ),
};

export const GeoEventReports = (
  props: GeoEventReportsProps,
): JSX.Element | null => {
  const { geoEventId, geoEventType } = props;
  const { classes } = useStyles();
  const { data: permissionsData } = usePermissions({
    geoEventId,
    geoEventType,
  });
  const isEditDisabled = isEditReportDisabled(geoEventType, permissionsData);
  const { cacheBusterTs } = useCacheState();
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const heightCache = useRef(new Map<number, number>());
  const { geoEvent } = useGeoEvent<Wildfire | Flooding>({
    id: geoEventId,
    useCacheBuster: true,
  });

  const query = useInfiniteQuery({
    queryKey: ['infiniteReports', geoEventId, cacheBusterTs],
    queryFn: ({ pageParam }) =>
      fetchReports(
        geoEventId,
        { limit: PAGINATION_LIMIT, offset: pageParam },
        cacheBusterTs,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.data.next ? extractOffset(lastPage.data.next) : null;
    },
    refetchInterval: MAIN_REFETCH_INTERVAL_MS,
    // Holding the previous data keeps status out of 'pending' when cacheBusterTs expires and
    // changes the key, so the list is not torn down and rebuilt (which reloads every embed).
    placeholderData: keepPreviousData,
  });

  const { fetchNextPage, hasNextPage, data, status, isFetchingNextPage } =
    query;

  const reports = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.data.results) ?? []).filter(
        Boolean,
      ) as Report[],
    [data?.pages],
  );

  // Tracks how many reports polling has prepended so Virtuoso can hold the scroll position. Reports come back newest
  // first, so the previously topmost report's new index is exactly the number added above it. If it is gone entirely
  // we can't tell how far the list moved so we leave the anchor alone.
  //
  // Lowering firstItemIndex makes Virtuoso scroll the drawer down by the height of the prepended reports, and it does
  // that no matter where the reader is. That is only what we want mid-list. At the top of the drawer we leave the
  // anchor alone so the new report simply pushes in from above, otherwise reopening a drawer whose cached reports are
  // refetched with a newer report on top scrolls the incident details out of view.
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUOSO_START_INDEX);
  const [topReportId, setTopReportId] = useState<number | null>(null);
  const currentTopReportId = reports[0]?.id ?? null;

  const { openEditReportActionMenu } = useEditReportActionMenuState();

  const handleEditClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, report: Report): void => {
      openEditReportActionMenu({
        report,
        geoEventType,
        anchorEl: event.currentTarget,
      });
    },
    [openEditReportActionMenu, geoEventType],
  );

  if (currentTopReportId !== topReportId) {
    const prependedCount =
      topReportId === null
        ? 0
        : reports.findIndex((report) => report.id === topReportId);
    setTopReportId(currentTopReportId);
    if (prependedCount > 0 && (scrollElementRef?.current?.scrollTop ?? 0) > 0) {
      setFirstItemIndex((index) => index - prependedCount);
    }
  }

  const renderItemContent: ItemContent<Report, ReportsListContext> =
    useCallback(
      (_, report) => {
        const cachedHeight = heightCache.current.get(report.id);
        return (
          <div style={{ minHeight: cachedHeight }}>
            <ReportCard
              key={report.id}
              media={getReportMedia(report.media)}
              report={report}
              className={classes.borderBottom}
              geoEventType={geoEventType}
              geoEventIsActive={Boolean(geoEvent?.isActive)}
              isEditReportDisabled={isEditDisabled}
              onEditClick={handleEditClick}
            />
          </div>
        );
      },
      [
        classes.borderBottom,
        geoEventType,
        geoEvent?.isActive,
        isEditDisabled,
        handleEditClick,
      ],
    );

  /*
   * React Virtuoso needs to figure out item heights on first render. This is easy if items
   * have identical heights or we know them beforehand. Since it only renders a handful of items
   * at a time (based on scroll position), it estimates heights for non-rendered items. E.g.,
   * with 20 items but only 3 visible (plus 3 buffer - see increaseViewportBy prop), it
   * calculates heights for those 6 items and estimates the rest.
   *
   * Scrolling down works smoothly as new items render and get measured. Scrolling up can be
   * tricky - if an item was estimated at 90px but is actually 80px, you'll see a "jump" during
   * correction.
   *
   * To mitigate this, we cache maximum item heights during scrolling. Since these items were
   * rendered, their heights are reliable for smoother upward scrolling.
   *
   * IMPORTANT: Do not cache heights for items with embedded URLs (iframes). The cached max height
   * is typically larger than actual content height, likely due to iframe content's margins.
   * This creates scrolling inconsistencies - see https://virtuoso.dev/#caveats for more details.
   *
   * While caching greatly improves general scrolling, it should be disabled for iframe content
   * to ensure proper rendering and scroll behavior.
   */
  const handleItemSize: SizeFunction = useCallback((el) => {
    const elId = el.dataset.id ? parseInt(el.dataset.id, 10) : null;
    const elHeight = el.getBoundingClientRect().height;

    if (!elId) return elHeight;

    const hasIframe = el.getElementsByTagName('iframe').length > 0;
    if (hasIframe) return elHeight;

    const cachedHeight = heightCache.current.get(elId);
    if (cachedHeight && elHeight <= cachedHeight) return cachedHeight;

    heightCache.current.set(elId, elHeight);
    return elHeight;
  }, []);

  const computeItemKey: ComputeItemKey<Report, ReportsListContext> =
    useCallback((_: number, report: Report) => report.id, []);

  const handleEndReached = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const pageCount = data?.pages.length ?? 0;

  const listContext: ReportsListContext = useMemo(
    () => ({
      isFetchingNextPage,
      bottomReached: !hasNextPage && pageCount > 1,
    }),
    [isFetchingNextPage, hasNextPage, pageCount],
  );

  // Ensures consistent virtuoso list rendering by setting minimum height
  // Required because Virtuoso fails to render properly with undefined height values
  const style = useMemo(() => ({ minHeight: reports.length * 96 }), [reports]);

  const deviceInfo = getDeviceInfo();

  // We do not cache item heights on iOS/Safari because it causes some blank spaces,
  // but we acknowledge there may be some jumpiness when scrolling up instead.
  // See: https://github.com/watch-duty/watchduty-frontend/pull/1177#issuecomment-3115128723
  const shouldCacheItemsHeight = useMemo(() => {
    if (Capacitor.isNativePlatform()) {
      return Capacitor.getPlatform() !== 'ios';
    }
    if (deviceInfo?.operatingSystem === 'ios') {
      return false;
    }
    return !isSafari();
  }, [deviceInfo?.operatingSystem]);

  const viewportBuffer = Math.min(window.innerHeight * 3, 2000); // Max 2000px buffer

  if (status === 'pending') {
    return <LoadingSpinner />;
  }

  const commonVirtuosoProps = {
    // DO NOT also pass totalCount. Virtuoso pipes data.length into the same internal count that the totalCount prop
    // writes to, and the server's report count disagrees with the number loaded so far. Both moving at once on a
    // poll gives Virtuoso two conflicting signals that the list grew, and things don't work too well then.
    data: reports,
    endReached: handleEndReached,
    increaseViewportBy: viewportBuffer,
    customScrollParent: scrollElementRef?.current as HTMLElement,
    itemContent: renderItemContent,
    computeItemKey,
    components: virtuosoComponents,
    context: listContext,
    firstItemIndex,
    skipAnimationFrameInResizeObserver: true,
    style,
  };

  return (
    <Virtuoso
      {...commonVirtuosoProps}
      /* itemSize has to be absent, not undefined. Virtuoso publishes every optional prop key
        that is present, so an explicit undefined would overwrite the sizer function seeded
        in its itemSize stream. */
      {...(shouldCacheItemsHeight ? { itemSize: handleItemSize } : {})}
    />
  );
};
